import React, { Component } from 'react'
import { shape, func, bool, arrayOf, number, string } from 'prop-types'
import classNames from 'classnames'
import SugarcubeContext from 'apps/SugarcubeContext'
import { GET_INFO_CANVAS_PIN } from 'models/tracking/constants'
import { getClientEventCoords, getMultiTouchAngle, isMultiTouchEvent } from 'utils/events'

require('./CompositionItemDraggable.scss')

class CompositionItemDraggable extends Component {
    static optionsMD(e) {
        // Stop the mouseDown if we're clicking
        // on options so that the dragStart doesn't
        // interfere
        e.stopPropagation()
    }

    constructor(props) {
        super(props)
        this.cornerRef = React.createRef()
        this.dragWrapper = React.createRef()

        this.startDrag = this.startDrag.bind(this)
        this.whileDragging = this.whileDragging.bind(this)
        this.cancelDrag = this.cancelDrag.bind(this)
        this.updateScale = this.updateScale.bind(this)
        this.getConvertedCoordinates = this.getConvertedCoordinates.bind(this)
        this.getSceneToDisplayCoordinates = this.getSceneToDisplayCoordinates.bind(this)
        this.onMouseDown = this.onMouseDown.bind(this)
        this.onMouseUp = this.onMouseUp.bind(this)
        this.onMouseMove = this.onMouseMove.bind(this)
        this.updateCenterPointStyle = this.updateCenterPointStyle.bind(this)
        this.getCorners = this.getCorners.bind(this)

        this.scaleX = 1
        this.scaleY = 1

        this.dragOffset = [0, 0]

        this.state = {
            isDragging: false,
            isMouseDown: false,
            centerStyle: null,
        }

        this.x = props.x
        this.y = props.y

        this.lastConvertedCoordinates = null
        this.lastTouchAngle = null // Multi-touch rotate
        this.lastX = 0

        this.mouseDownLocation = [0, 0]
    }

    componentDidMount() {
        this.updateScale()

        const { current: wrapperCurrent } = this.dragWrapper
        if (wrapperCurrent) {
            wrapperCurrent.addEventListener('touchstart', this.onMouseDown)
            wrapperCurrent.addEventListener('touchmove', this.onMouseMove)
            wrapperCurrent.addEventListener('touchend', this.cancelDrag)
            wrapperCurrent.addEventListener('touchcancel', this.cancelDrag)
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.x !== this.lastX) {
            this.lastX = this.props.x
        }
        if (this.props.x !== this.x) {
            this.x = this.props.x
            this.updateCenterPointStyle()
        }
        if (this.props.y !== this.y) {
            this.y = this.props.y
            this.updateCenterPointStyle()
        }
        if (!this.state.centerStyle) {
            this.updateCenterPointStyle()
        }
        if (this.props.corners !== prevProps.corners) {
            this.updateCenterPointStyle()
        }
    }

    componentWillUnmount() {
        window.removeEventListener('mouseup', this.cancelDrag)
    }

    onMouseDown(event) {
        // This check will allow unselected items to
        // trigger the deselect-all function in the
        // preview component
        if (this.props.selected) {
            event.stopPropagation()
        }
        const { clientX, clientY } = getClientEventCoords(event)
        this.mouseDownLocation = [clientX, clientY]
        this.setState({ isMouseDown: true })
    }

    onMouseUp() {
        this.setState({ isMouseDown: false })
    }

    onMouseMove(event) {
        const { isMobile } = this.context
        const { isDragging } = this.state
        const { selected } = this.props
        const { clientX, clientY, isTouch } = getClientEventCoords(event)
        event.preventDefault() // Don't back nav on touch drag

        if (isMobile || (isTouch && !selected)) {
            return
        }

        if (isDragging) {
            this.whileDragging(event)
        } else if (this.state.isMouseDown) {
            const thresholdExceeded =
                Math.abs(clientX - this.mouseDownLocation[0]) > 3 || Math.abs(clientY - this.mouseDownLocation[1]) > 3

            if (this.props.selected || thresholdExceeded) {
                this.startDrag(event)
            }
        }
    }

    getConvertedCoordinates(clientX, clientY) {
        // Convert from window-scale coordinates
        // to scene and Preview scale coordinates
        const corner = this.cornerRef.current
        const { left, top, width, height } = corner.getBoundingClientRect()
        const { scaleX, scaleY } = this

        const eventX = clientX - left
        const eventY = clientY - top

        const x = eventX * scaleX
        const y = eventY * scaleY

        // Percentage values
        const relX = (eventX / width) * 100
        const relY = (eventY / height) * 100

        return {
            x,
            y,
            relX,
            relY,
        }
    }

    getSceneToDisplayCoordinates(sceneX, sceneY) {
        // Convert from scene coordinates to Preview-scale
        // percentage values
        const corner = this.cornerRef.current
        if (!corner) {
            return null
        }
        const { width, height } = corner.getBoundingClientRect()
        const { scaleX, scaleY } = this

        const x = sceneX / scaleX
        const y = sceneY / scaleY

        const relX = (x / width) * 100
        const relY = (y / height) * 100

        return {
            x,
            y,
            relX,
            relY,
        }
    }

    getCorners() {
        // Corner layout (unit: percentage)
        // |0 1|
        // |3 2|

        const { corners } = this.props
        if (!corners) {
            return {
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bottomPadded: 0,
                farRight: 0,
                farBottom: 0,
            }
        }

        const top = corners[0][1] * 100
        const left = corners[0][0] * 100
        const farBottom = corners[2][1] * 100
        const farRight = corners[1][0] * 100

        // If the distance between sides
        // is too small, pad it
        const paddingX = 0
        const paddingY = 0

        return {
            // Corner relative
            bottom: Math.max(0, 100 - farBottom - paddingY),
            top: Math.max(0, top - paddingY),
            left: Math.max(0, left - paddingX),
            right: Math.max(0, 100 - farRight - paddingX),
            // Padded for dimensionsContainer
            bottomPadded: Math.max(100 - farBottom - 4, 0),
            // Top-left relative
            farRight: Math.min(100, farRight + paddingX),
            farBottom: Math.min(100, farBottom + paddingY),
        }
    }

    updateScale() {
        /*
            We need to calculate the scale between
            the depthmap and the drag handle because
            the drag handle is rendered relative to the
            dom while we want to save the x,y coords
            relative to the depthmap
            */

        const renderDimensions = this.props.renderDimensions
        if (!renderDimensions) {
            return
        }

        const previewRect = this.cornerRef.current.getBoundingClientRect()
        const scaleX = renderDimensions[0] / previewRect.width
        const scaleY = renderDimensions[1] / previewRect.height

        this.scaleX = scaleX
        this.scaleY = scaleY
    }

    startDrag(event) {
        this.updateScale()
        this.setState({ isDragging: true })
        window.addEventListener('mouseup', this.cancelDrag)
        this.props.onDragStart()
        const corner = this.cornerRef.current
        const { left, top } = corner.getBoundingClientRect()
        const { clientX, clientY } = getClientEventCoords(event)

        // Don't snap to center
        const centerPoint = this.getSceneToDisplayCoordinates(this.x, this.y)
        this.dragOffset = [clientX - left - centerPoint.x, clientY - top - centerPoint.y]

        const renderObject = this.props.getRenderObject()
        if (renderObject) {
            this.headingTarget = renderObject.getOrientation()
        } else {
            this.headingTarget = 0
        }
    }

    whileDragging(event) {
        if (!event) {
            return
        }
        if (event.stopPropagation) {
            event.stopPropagation()
        }

        const { isDragging } = this.state
        if (isDragging) {
            let overrideHeading = false

            const { clientX, clientY } = getClientEventCoords(event)

            const convertedCoordinates = this.getConvertedCoordinates(
                clientX - this.dragOffset[0],
                clientY - this.dragOffset[1],
            )

            if (this.props.rotating) {
                if (this.lastConvertedCoordinates !== null) {
                    // The value 100 for dxScale was chosen
                    // empirically through trial and error. If
                    // dxScale was 1 then one pixel of mouse
                    // motion would produce 1 radian of rotation,
                    // which is way too much. Rotation needs to
                    // be less sensitive than that.

                    const dxScale = 100
                    const dx = convertedCoordinates.x - this.lastConvertedCoordinates.x
                    const dh = dx / dxScale
                    this.headingTarget += dh
                }

                this.lastConvertedCoordinates = convertedCoordinates
            } else if (isMultiTouchEvent(event)) {
                overrideHeading = true

                const rotateAngle = getMultiTouchAngle(event)

                if (this.lastTouchAngle !== null) {
                    const deltaAngle = rotateAngle - this.lastTouchAngle
                    this.headingTarget -= deltaAngle
                }

                this.lastTouchAngle = rotateAngle
            } else {
                this.x = convertedCoordinates.x
                this.y = convertedCoordinates.y
            }

            const renderObject = this.props.getRenderObject()
            if (renderObject) {
                this.props.onDrag({
                    x: this.x,
                    y: this.y,
                    heading: this.headingTarget,
                    timeStamp: event.timeStamp,
                    overrideHeading,
                })
            }
        }
    }

    cancelDrag(event) {
        const { isDragging, isMouseDown } = this.state
        if (isMouseDown && !isDragging) {
            // Handle "click" manually to prevent click events
            // when dragging
            this.props.onClick(event)
        }

        this.onMouseUp()
        event.stopPropagation()
        window.removeEventListener('mouseup', this.cancelDrag)

        if (isDragging) {
            this.setState({ isDragging: false })
            this.lastX = this.x
            this.lastConvertedCoordinates = null
            this.lastTouchAngle = null
            this.props.onDragFinish()
        }
    }

    updateCenterPointStyle() {
        let centerStyle = null

        const corners = this.getCorners()
        centerStyle = {
            top: `${corners.top + (corners.farBottom - corners.top) / 2}%`,
            left: `${corners.left + (corners.farRight - corners.left) / 2}%`,
            maxWidth: `${corners.farRight - corners.left}%`,
        }

        this.setState({ centerStyle })
    }

    render() {
        const {
            className,
            isUnderCursor,
            isCurrentDragValid,
            rotating,
            onClick,
            options,
            name,
            price,
            description,
            openSettings,
            selected,
            revealDots,
            showDot,
        } = this.props
        const { isDragging, centerStyle, isMouseDown } = this.state
        const { isMobile } = this.context

        const corners = this.getCorners()

        const boxStyle = {
            left: `${corners.left}%`,
            top: `${corners.top}%`,
            right: `${corners.right}%`,
            bottom: `${selected ? corners.bottom : corners.bottomPadded}%`,
        }

        const optionsStyle = {
            top: `${corners.top}%`,
            bottom: `${corners.bottom}%`,
        }
        if (corners.right < 4) {
            optionsStyle.right = `${100 - corners.left}%`
        } else {
            optionsStyle.left = `${100 - corners.right}%`
        }

        const itemClass = {
            dragging: isDragging,
            isUnderCursor,
            rotating,
            isInvalid: !isCurrentDragValid,
        }

        return (
            <div
                className={classNames('CompositionItemDraggable', className, itemClass)}
                key="compArtItem"
                onMouseUp={this.cancelDrag}
            >
                {!isMobile && (
                    <div
                        className="dragWrapper"
                        style={boxStyle}
                        ref={this.dragWrapper}
                        onMouseDown={this.onMouseDown}
                        onMouseMove={this.onMouseMove}
                        onMouseUp={this.cancelDrag}
                        onBlur={this.cancelDrag}
                        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                        tabIndex={0}
                        onFocus={(e) => {
                            if (!isMouseDown) {
                                onClick(e)
                            }
                        }}
                    >
                        {this.props.children}
                    </div>
                )}
                <div className="optionsWrapper" style={optionsStyle}>
                    {options}
                </div>
                <div className="cornersArea" ref={this.cornerRef} />
                {showDot && !isMobile && (
                    <div
                        className={classNames('dotWrapper', { revealDots })}
                        style={
                            this.props.tagPoint
                                ? { top: `${this.props.tagPoint.y}%`, left: `${this.props.tagPoint.x}%` }
                                : centerStyle
                        }
                        onClick={onClick}
                    >
                        <div className="outerDot">
                            <div className="innerDot" />
                        </div>
                        <div
                            className="infoBoxContainer"
                            onClick={(e) => {
                                e.stopPropagation()
                                openSettings(GET_INFO_CANVAS_PIN)
                            }}
                        >
                            <div className="infoBox">
                                <div className="infoBoxHeaderContainter">
                                    <div className="infoBoxHeader">
                                        <div className="infoBoxTitle">{name}</div>
                                        {description && <div className="infoBoxDescription">{description}</div>}
                                    </div>
                                </div>
                                {price && <div className="infoBoxPrice">{price}</div>}
                            </div>
                        </div>
                    </div>
                )}
                {showDot && isMobile && (
                    <div
                        className={classNames('dotWrapper', 'revealDots', 'mobileDot')}
                        style={
                            this.props.tagPoint
                                ? { top: `${this.props.tagPoint.y}%`, left: `${this.props.tagPoint.x}%` }
                                : centerStyle
                        }
                        onClick={onClick}
                    >
                        <div className="outerDot">
                            <div className="innerDot" />
                        </div>
                    </div>
                )}
            </div>
        )
    }
}

CompositionItemDraggable.contextType = SugarcubeContext

CompositionItemDraggable.defaultProps = {
    corners: null,
    tagPoint: null,
    rotating: false,
    options: null,
    description: null,
    price: null,
    openSettings: null,
    renderDimensions: null,
    onDrag: null,
    onClick: null,
    children: null,
}

CompositionItemDraggable.propTypes = {
    className: string.isRequired,
    isUnderCursor: bool.isRequired,
    isCurrentDragValid: bool.isRequired,
    corners: arrayOf(arrayOf(number).isRequired),
    renderDimensions: arrayOf(number),
    onDragStart: func.isRequired,
    onDrag: func,
    onDragFinish: func.isRequired,
    onClick: func,

    tagPoint: shape({}),

    children: shape({}),
    options: shape({}),

    x: number.isRequired,
    y: number.isRequired,

    rotating: bool,
    selected: bool.isRequired,
    name: string.isRequired,
    description: string,
    price: string,
    openSettings: func,
    getRenderObject: func.isRequired,
    revealDots: bool.isRequired,
    showDot: bool.isRequired,
}

export default CompositionItemDraggable
